/**
 * src/App.tsx — the application shell: providers, toast surface, router, three routes.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/App.tsx` (whole file, 33 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §1.2 (provider nesting, exact order),
 *         §1.3 (route table and gating), §12 (toast systems), §15 D-7, §16 rank 9.
 *
 * ═══ PROVIDER NESTING IS REPRODUCED EXACTLY (spec §1.2) ══════════════════════════════════════
 *     QueryClientProvider   (App.tsx:14)
 *       AuthLayer           (App.tsx:15)  ← Suspense → HexclaveProvider → HexclaveTheme →
 *         TooltipProvider   (App.tsx:16)     HexclaveAuthProvider. The source's single
 *           <Toaster/>      (App.tsx:17)     `AuthProvider` sat at this position; everything
 *           <Sonner/>       (App.tsx:18)     Hexclave adds wraps *outside* it, so the relative
 *           BrowserRouter   (App.tsx:19)     order below is untouched.
 *             Routes        (App.tsx:20)
 *
 * Consequences carried over verbatim, all of them deliberate:
 *  - The toast surface sits **outside** `BrowserRouter` (`App.tsx:17-19`), so a toast can never
 *    contain a router `<Link>`. Do not move it inside to "enable" that.
 *  - The auth provider is **inside** `QueryClientProvider`, which is what lets `use-database`'s
 *    hooks read `user?.id` for their query keys; and it is **outside** the router, so a logout does
 *    not remount the tree.
 *  - `TooltipProvider` is mounted once, here. `Toolbar` therefore renders bare `<Tooltip>`s with no
 *    provider of their own (`Toolbar.tsx:35`).
 *
 * ═══ `new QueryClient()` TAKES NO OPTIONS — THAT IS LOAD-BEARING (spec §1.2, `App.tsx:11`) ════
 * React Query v5 defaults therefore apply and are part of the observable behaviour being validated:
 *   staleTime: 0 · gcTime: 5 min · retry: 3 with exponential backoff · refetchOnWindowFocus: true ·
 *   refetchOnMount: true.
 * Every window focus refetches every mounted list, and every failed request is retried three times
 * before `isError` is ever set — which is part of why DF-22 rank 5 ("a server outage looks like an
 * empty account") takes several seconds to even settle. Do not pass `defaultOptions`. Do not set
 * `retry: false`. Do not add a `QueryCache` `onError` handler — that would be the global 401/403
 * handling spec §2.4 confirms the app does not have.
 *
 * ═══ THE ONE APPROVED DEVIATION — DF-22 RANK 9, DUPLICATE TOASTS ═════════════════════════════
 * The source mounts **two** toast systems side by side (`App.tsx:17-18`): shadcn's Radix `<Toaster/>`
 * — used by `AuthContext` and every mutation in `use-database.ts` — and `<Sonner/>`, used by
 * `ConverterApp`, `Toolbar`, both save dialogs, `FolderTree`, `MoveDialog` and the sidebar. One
 * successful save therefore raises two notifications in two different widgets (spec §12).
 *
 * That is DF-22 rank 9, and it is **the only defect in the list this port does not reproduce**, by
 * owner approval (DF-21, `DEFERRED-FIXES.md`): current shadcn no longer ships the Radix toast and
 * only `sonner` is installed here. So exactly one `<Sonner/>` is mounted, at the source's line 18,
 * and the data layer's toasts were mapped onto sonner mechanically (see `AuthContext.tsx`'s header).
 *
 * **This changes what you will see when validating rank 9.** Two things survive the substitution and
 * are NOT to be "finished off":
 *   1. shadcn's `TOAST_LIMIT = 1` made a new toast *replace* the previous one; sonner stacks. So the
 *      pair now appears as two stacked cards instead of one card in each of two corners.
 *   2. Actions that toast from two call sites still toast twice — a document save fires
 *      `use-database.ts`'s `'Document saved'` **and** `SaveDocumentDialog`'s
 *      `'Document saved successfully'`; an upload fires `Toolbar`'s `'File loaded successfully'` and
 *      `ConverterApp`'s `'Full document loaded'`. Both call sites were sonner in the source for the
 *      upload case. Removing either is a real fix and is out of scope.
 *
 * `src/components/ui/sonner.tsx` calls `useTheme()` from `next-themes` while **no `ThemeProvider` is
 * mounted anywhere** (spec §1.2 last bullet). `next-themes` falls back to its default context, so the
 * theme resolves to `"system"`. Reproduced — do not add a `ThemeProvider`.
 *
 * ═══ ROUTES (spec §1.3) ══════════════════════════════════════════════════════════════════════
 *   `/`       → `<Index/>`      — gated by `ProtectedRoute`, which renders `<HexclaveAuthForm/>` IN PLACE
 *                                 when logged out (the URL stays `/`).
 *   `/admin`  → `<AdminPage/>`  — **not** wrapped in `ProtectedRoute`; it self-gates and redirects
 *                                 non-admins to `/` (`Admin.tsx:18-20`). An unauthenticated visitor
 *                                 to `/admin` is bounced to `/` and meets the login form there, so
 *                                 the login form never renders at `/admin`.
 *   `*`       → `<NotFound/>`   — **not auth-gated at all.** An anonymous visitor to `/anything`
 *                                 sees the 404 page rather than the login form. Spec §1.3 flags this
 *                                 and asks that it be preserved or changed deliberately: preserved.
 *
 * The source's own comment above the catch-all is kept verbatim.
 *
 * ═══ NOT PRESENT, ON PURPOSE ═════════════════════════════════════════════════════════════════
 *  - **No error boundary** (DF-22 rank 6 / spec §15 D-19). A render throw anywhere below — e.g.
 *    `format(new Date(user.created_at), 'PPP p')` in `Admin.tsx` — unmounts the whole tree and the
 *    user gets a white page. That is the long-standing "blank page" report and the owner is
 *    validating it visually before it is fixed. Do not wrap `<Routes>` in anything.
 *  - No `beforeunload` guard, no autosave, no keyboard shortcuts (DF-22 ranks 1 and 15).
 *  - No `<ThemeProvider>`, no `basename`, no lazy routes, no `Suspense`, no scroll restoration.
 */
import { Suspense, type ReactNode } from 'react'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { HexclaveHandler, HexclaveProvider, HexclaveTheme } from '@hexclave/react'
import { HexclaveAuthProvider } from '@/contexts/HexclaveAuthProvider'
import { hexclaveApp } from '@/lib/hexclave'
import Index from './pages/Index'
import AdminPage from './pages/Admin'
import NotFound from './pages/NotFound'

// `App.tsx:11` — no options. See the header: React Query's defaults are part of the contract.
const queryClient = new QueryClient()

/**
 * The auth layer. One path — the `VITE_AUTH_MODE` build-time branch and the legacy `AuthProvider`
 * were deleted on 2026-08-09 (owner instruction, plan §10 step 3).
 *
 * ═══ THE NESTING IS STILL EXACTLY THE SOURCE'S ═════════════════════════════════════════════════
 * Everything Hexclave adds wraps **outside** the app's own auth provider, so the relative order the
 * spec pins — `QueryClientProvider` → auth → `TooltipProvider` → toaster → router — is unchanged.
 * `HexclaveAuthProvider` sits inside `QueryClientProvider` (so `use-database`'s query keys can read
 * `user?.id`) and outside the router (so a logout does not remount the tree).
 *
 * The `Suspense` boundary is **required**, not decorative: `useUser` and the rest of the `useXyz`
 * family suspend until their data is available, and React throws without a boundary above them. Its
 * fallback deliberately says nothing about Hexclave — it is a React requirement, and anything below
 * may be what is actually loading.
 */
const AuthLayer = ({ children }: { children: ReactNode }): ReactNode => (
  <Suspense
    fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    }
  >
    <HexclaveProvider app={hexclaveApp()}>
      <HexclaveTheme>
        <HexclaveAuthProvider>{children}</HexclaveAuthProvider>
      </HexclaveTheme>
    </HexclaveProvider>
  </Suspense>
)

/**
 * `/handler/*` — sign-in, sign-up, password reset, email verification, OAuth callback and sign-out,
 * rendered by the SDK **on this origin**.
 *
 * This route is what makes `urls.default.type: 'handler-component'` work (`src/lib/hexclave.ts`).
 * The two are a pair: without this route every auth link 404s, and without that setting the SDK
 * sends users to `<project-id>.built-with-hexclave.com` instead. **Change them together.**
 *
 * `location` is passed explicitly because `HexclaveHandler` otherwise reads `window.location`
 * directly, which is invisible to React Router — a client-side navigation would leave it rendering
 * the previous page.
 *
 * Deliberately **not** wrapped in `ProtectedRoute`: these are the pages a signed-out visitor needs.
 * Gating them would make signing in require being signed in.
 */
const AuthHandlerRoute = () => {
  const { pathname } = useLocation()
  return <HexclaveHandler fullPage app={hexclaveApp()} location={pathname} />
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthLayer>
      <TooltipProvider>
        {/*
          `App.tsx:17` mounted shadcn's <Toaster/> here. Dropped — DF-22 rank 9, the single
          approved deviation. Only sonner is installed in this repo.
        */}
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* Hexclave's auth pages, served from this origin — see AuthHandlerRoute. */}
            <Route path="/handler/*" element={<AuthHandlerRoute />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthLayer>
  </QueryClientProvider>
)

export default App
